import { Injectable } from "@angular/core";
import { wsRoutes, RouteConfigInstance } from "../../projects/web/src/app/routes.config";
import { AccountsService, Login } from "../accounts.service";
import { PermissionResolverService } from "./permission-resolver.service";

/**
 * Default in-app URL after login, and fallback when a guarded route is denied.
 * `/` is homepage. Direct links go to `/login?redirectTo=…` then back after auth.
 */
@Injectable({
  providedIn: "root",
})
export class LandingRouteService {
  constructor(
    private readonly permissionResolver: PermissionResolverService,
    private readonly accounts: AccountsService
  ) {}

  /**
   * After login: `redirectTo` when the user hit a protected URL while logged out,
   * otherwise `/` (homepage). Super Admin picker is not the post-login landing.
   */
  pathAfterLogin(_login?: Login | null, redirectTo?: string | null): string {
    return sanitizeRedirectTo(redirectTo) || "/";
  }

  path(excludePath?: string): string {
    const exclude = normalizePath(excludePath);
    for (const route of wsRoutes) {
      if (!route.showInMenu || !route.path || route.redirectTo) {
        continue;
      }
      if (!this.canAccess(route)) {
        continue;
      }
      if (route.children?.length) {
        const child = route.children.find(
          (c) => c.showInMenu && !!c.path && this.canAccess(c)
        );
        if (child?.path) {
          const full = normalizePath(`/${route.path}/${child.path}`);
          if (full !== exclude) {
            return full;
          }
        }
      }
      const parent = normalizePath(`/${route.path}`);
      if (parent !== exclude) {
        return parent;
      }
    }
    return exclude === "/reports-dashboard" ? "/calendar" : "/reports-dashboard";
  }

  canAccess(route: Pick<RouteConfigInstance, "permission" | "permissionSectionBase">): boolean {
    if (route.permissionSectionBase) {
      return this.permissionResolver.canViewSection(route.permissionSectionBase);
    }
    if (route.permission) {
      return this.accounts.hasPermission(route.permission);
    }
    return true;
  }
}

/** Same-app protected path only; rejects public, login, and external URLs. */
export function sanitizeRedirectTo(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  let decoded = value.trim();
  try {
    decoded = decodeURIComponent(decoded).trim();
  } catch {
    return null;
  }
  if (!decoded.startsWith("/") || decoded.startsWith("//")) {
    return null;
  }
  if (isPublicAppPath(decoded)) {
    return null;
  }
  return decoded;
}

/** Unauthenticated pages (shell hides chrome). `/` is homepage after login, not public. */
export function isPublicAppPath(url: string): boolean {
  const pathOnly = (url || "").split("?")[0].split("#")[0];
  if (!pathOnly || pathOnly === "/") {
    return false;
  }
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathOnly === prefix || pathOnly.startsWith(`${prefix}/`)
  );
}

const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/registration",
  "/unsubscribe",
  "/reset-password",
  "/set-password",
  "/mfa-reenroll",
  "/privacy",
] as const;

function normalizePath(path?: string | null): string {
  if (!path) {
    return "";
  }
  const noQuery = path.split("?")[0].split("#")[0];
  const withSlash = noQuery.startsWith("/") ? noQuery : `/${noQuery}`;
  return withSlash.replace(/\/+$/, "") || "/";
}
